import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

export function usePushNotifications() {
    const [permission, setPermission] = useState(
        typeof Notification !== 'undefined' ? Notification.permission : 'denied'
    );
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [loading, setLoading] = useState(false);
    const [supported, setSupported] = useState(false);

    useEffect(() => {
        const ok = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
        setSupported(ok);
        if (!ok) return;

        // Check if already subscribed
        navigator.serviceWorker.ready.then(reg => {
            reg.pushManager.getSubscription().then(sub => {
                setIsSubscribed(!!sub);
            });
        });
    }, []);

    const subscribe = useCallback(async () => {
        if (!supported) return false;
        setLoading(true);
        try {
            const perm = await Notification.requestPermission();
            setPermission(perm);
            if (perm !== 'granted') {
                setLoading(false);
                return false;
            }

            // Get VAPID key from backend
            const { data } = await api.get('/push/vapid-key');
            const vapidKey = urlBase64ToUint8Array(data.public_key);

            const reg = await navigator.serviceWorker.ready;
            const subscription = await reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: vapidKey
            });

            // Send subscription to backend
            await api.post('/push/subscribe', {
                subscription: subscription.toJSON()
            });

            setIsSubscribed(true);
            return true;
        } catch (err) {
            console.error('Push subscribe error:', err);
            return false;
        } finally {
            setLoading(false);
        }
    }, [supported]);

    const unsubscribe = useCallback(async () => {
        if (!supported) return;
        setLoading(true);
        try {
            const reg = await navigator.serviceWorker.ready;
            const sub = await reg.pushManager.getSubscription();
            if (sub) {
                await api.post('/push/unsubscribe', { endpoint: sub.endpoint });
                await sub.unsubscribe();
            }
            setIsSubscribed(false);
        } catch (err) {
            console.error('Push unsubscribe error:', err);
        } finally {
            setLoading(false);
        }
    }, [supported]);

    return { permission, isSubscribed, subscribe, unsubscribe, loading, supported };
}
